import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  DisplayProps,
  HeightProps,
  JustifyContentProps,
  MarginLeftProps,
  MarginRightProps,
  OverflowProps,
  PositionProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  display,
  flex,
  height,
  justifyContent,
  marginLeft,
  marginRight,
  overflow,
  position,
  space,
  width,
  zIndex,
} from 'styled-system';

import { NextControl, PrevControl } from 'common/components/Carousel/controls/CarouselControls';
import { useCarouselNavigation } from 'common/components/Carousel/hooks/use-carousel-navigation';
import type { FlexProps } from 'common/components/Flex';
import { Flex } from 'common/components/Flex';
import { CAROUSEL_CONTROL_Z_INDEX } from 'common/constants/z-index';
import { usePromoEvents } from 'common/hooks/dynamic-yield';
import type { SpaceProps } from 'common/theme/system-types';
import type { FlexGapProps, ListStyleProps, OverflowXProps } from 'common/theme/system-utilities';
import { flexGap, listStyle, overflowX } from 'common/theme/system-utilities';
import type { CampaignTrackingData } from 'common/tracking/campaignEventData';
import { CommonTabBarChip } from './CommonTabBarChip';

interface Tab {
  isActive: boolean;
  text: string;
  link?: Nullable<string>;
}

interface CommonTabBarProps extends Partial<CampaignTrackingData> {
  activeTextColor?: string;
  activeBackgroundColor?: string;
  items: Tab[];
}

const Container = styled.nav<
  PositionProps & HeightProps & OverflowProps & WidthProps & ZIndexProps
>`
  ${position};
  ${height};
  ${overflow};
  ${width};
  ${zIndex};
`;

const containerStyles: ComponentProps<typeof Container> = {
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  zIndex: CAROUSEL_CONTROL_Z_INDEX,
};

const NavigationWrapper = styled.div<DisplayProps & JustifyContentProps & SpaceProps>`
  ${display};
  ${justifyContent};
  ${space};
`;

const navigationWrapperStyles: ComponentProps<typeof NavigationWrapper> = {
  display: 'flex',
  justifyContent: 'center',
};

export const List = styled(Flex)<FlexProps & ListStyleProps & OverflowXProps & FlexGapProps>`
  ${listStyle};
  ${overflowX};
  ${flexGap};

  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const listStyles: ComponentProps<typeof List> = {
  as: 'ul',
  p: 'sp_0',
  py: ['sp_8', null, 'sp_12'], // To hide horizontal scrollbar
  pl: '4px',
  m: 'sp_0',
  flexWrap: 'nowrap',
  listStyle: 'none',
  overflowX: 'auto',
  gap: 'sp_12',
};

const ListItem = styled.li<FlexProps & MarginLeftProps & MarginRightProps>`
  ${flex};
  ${marginLeft};
  ${marginRight};
`;

const listItemStyles: ComponentProps<typeof ListItem> = {
  flex: '0 0 auto',
};

const CommonTabBar = ({
  items,
  activeBackgroundColor,
  activeTextColor,
  dyDecisionId,
  dyVariationId,
  placement,
  name,
}: CommonTabBarProps) => {
  const { listRef, hasPrevControl, hasNextControl, updateControls, onPreviousSlide, onNextSlide } =
    useCarouselNavigation();

  const { onPromoView, onPromoClick } = usePromoEvents({
    dyDecisionId,
    dyVariationId,
    placement,
    name,
  });

  useEffect(() => {
    if (!name) {
      return;
    }
    onPromoView();
  }, [onPromoView]);

  return (
    <Container data-corgi-id="common-tab-bar" {...containerStyles}>
      <NavigationWrapper {...navigationWrapperStyles}>
        {hasPrevControl && <PrevControl handleClick={onPreviousSlide} isTabBar />}
        <List {...listStyles} ref={listRef} onScroll={updateControls}>
          {items.map((item, index) => (
            <ListItem
              key={index}
              {...listItemStyles}
              {...(index === 0 ? { ml: ['sp_16', 'sp_24', 'sp_0', null] } : {})}
              {...(index === items.length - 1
                ? {
                    mr: ['sp_4', 'sp_12', 'sp_0', null],
                  }
                : {})}
            >
              <CommonTabBarChip
                {...item}
                activeTextColor={activeTextColor}
                activeBackgroundColor={activeBackgroundColor}
                onClick={!item?.isActive && name ? onPromoClick : undefined}
              />
            </ListItem>
          ))}
        </List>
        {hasNextControl && <NextControl handleClick={onNextSlide} isTabBar />}
      </NavigationWrapper>
    </Container>
  );
};

export default CommonTabBar;
